import { useAudioPlayer } from '../../hooks/useAudioPlayer'
import './AudioPlayer.css'

// Placeholder album art
const PLACEHOLDER_ART = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="50" r="30" fill="none" stroke="#6E2EBF" stroke-width="2"/>
    <circle cx="50" cy="50" r="10" fill="#6E2EBF"/>
  </svg>
`)

// Convert image URL to thumbnail URL
function getThumbnailUrl(url) {
  if (!url || url.startsWith('data:')) return url
  const lastDot = url.lastIndexOf('.')
  const queryStart = url.indexOf('?')
  if (lastDot === -1) return url
  if (queryStart !== -1 && queryStart > lastDot) {
    return `${url.slice(0, lastDot)}_thumb${url.slice(lastDot)}`
  }
  return `${url.slice(0, lastDot)}_thumb${url.slice(lastDot)}`
}

// Get cover art URL, preferring thumbnail
function getCoverUrl(album) {
  const fullUrl = album?.images?.front || album?.coverArtUrl
  return fullUrl ? getThumbnailUrl(fullUrl) : PLACEHOLDER_ART
}

export default function AudioPlayer() {
  const {
    currentSong,
    currentAlbum,
    isPlaying,
    duration,
    currentTime,
    volume,
    setVolume,
    togglePlay,
    playNext,
    playPrevious,
    seekPercent
  } = useAudioPlayer()

  if (!currentSong) return null

  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const percent = ((e.clientX - rect.left) / rect.width) * 100
    seekPercent(Math.max(0, Math.min(100, percent)))
  }

  const handleVolumeChange = (e) => {
    setVolume(parseFloat(e.target.value))
  }

  return (
    <div className="audio-player">
      <div className="player-song-info">
        <div className="player-cover">
          <img
            src={getCoverUrl(currentAlbum)}
            alt={currentAlbum?.title || 'Album'}
            onError={(e) => {
              // Fallback to full image if thumbnail doesn't exist
              const fullUrl = currentAlbum?.images?.front || currentAlbum?.coverArtUrl
              if (fullUrl && !e.target.dataset.triedFull) {
                e.target.dataset.triedFull = 'true'
                e.target.src = fullUrl
              } else {
                e.target.src = PLACEHOLDER_ART
              }
            }}
          />
        </div>
        <div className="player-text">
          <span className="player-title">
            {currentSong.title}
            {currentSong.artist && currentSong.artist !== currentAlbum?.artist && (
              <em className="player-song-artist"> – {currentSong.artist}</em>
            )}
          </span>
          <span className="player-artist">{currentAlbum?.artist || 'Unknown Artist'}</span>
        </div>
      </div>

      <div className="player-controls">
        <div className="player-buttons">
          <button className="player-btn" onClick={playPrevious} title="Previous">
            ⏮
          </button>
          <button className="player-btn player-btn-play" onClick={togglePlay} title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button className="player-btn" onClick={playNext} title="Next">
            ⏭
          </button>
        </div>

        <div className="player-progress">
          <span className="player-time">{formatTime(currentTime)}</span>
          <div className="progress-bar" onClick={handleSeek}>
            <div className="progress-fill" style={{ width: `${progress}%` }} />
            <div className="progress-handle" style={{ left: `${progress}%` }} />
          </div>
          <span className="player-time">{formatTime(duration)}</span>
        </div>
      </div>

      <div className="player-volume">
        <span className="volume-icon">{volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          onChange={handleVolumeChange}
          className="volume-slider"
        />
      </div>
    </div>
  )
}
